import { AxiosError } from "axios";
import alertService from "../components/alert/alertService";
import { isHandledError } from "./axios";
import { ApiError } from "./ApiError";

export type AlertType = "error" | "info";

export interface CodeHandler {
  messageKey: string | ((body: string) => string);
  titleKey?: string;
  type?: AlertType;
  afterAlert?: () => void;
}

export type CodeMap = Record<string, CodeHandler | string>;

export const defaultCodeMap: CodeMap = {
  FORBIDDEN_ACCESS_ERROR: "Forbidden-Access-Error",
  VALIDATION_ERROR: "validation-failed",
  EVIDENCE_FILE_NOT_UPLOADED: "Evidence-File-Not-Uploaded",
  FILE_SIZE_EXCEED: "File-Size-Exceeded",
  PLAN_EXPIRED: {
    messageKey: "PLAN_EXPIRED",
    titleKey: "Please-Upgrade-Package",
    type: "info",
  },
  FORBIDDEN_ACTION_ERROR: {
    messageKey: "FORBIDDEN_ACTION_ERROR",
    titleKey: "Please-Upgrade-Package",
    type: "info",
  },
  SUBORGANIZATION_ALREADY_EXISTS: "SUBORGANIZATION_ALREADY_EXISTS",
  ACTIVITY_UPSERT_PROCESS_FAILED: "Forbidden-Access-Error",
  FORBIDDEN_UPDATE_RESTRICTED_FIELDS: "Forbidden-Update-Restricted-Fields",
  CANNOT_DELETE_GENERATED_MONTHLY_ACTIVITY: "Cannot-Delete-Generated-Monthly-Activity",
  CANNOT_UPDATE_TEMPLATE_ACTIVITY: "Cannot-Update-Template-Activity",
  ACTIVITY_MULTIPLE_SOFT_DELETE_PROCESS_FAILED:
    "Activity-Multiple-Soft-Delete-Process-Failed",
  RESOURCE_ALREADY_IN_USE: "Resource-Already-In-Use",
  RESOURCE_NOT_FOUND: "noResultsOverlayLabel",
  ACTIVITIES_HISTORY_INVALID_STATUS: "Activities-History-Invalid-Status",
  // Transient infra fault, not a permissions problem: without this it fell through to the access-denied fallback.
  SERVICE_UNAVAILABLE: {
    messageKey: "Service-Unavailable-Error",
    type: "info",
  },
};

const normalizeEntry = (entry: CodeHandler | string): CodeHandler =>
  typeof entry === "string" ? { messageKey: entry, type: "error" } : entry;

const showAlert = (handler: CodeHandler, body: string): void => {
  const message =
    typeof handler.messageKey === "function"
      ? handler.messageKey(body)
      : handler.messageKey;

  const promise = handler.type === "info"
    ? alertService.info(message, handler.titleKey)
    : alertService.error(message, handler.titleKey);

  if (handler.afterAlert) {
    const cb = handler.afterAlert;
    Promise.resolve(promise).then(() => cb());
  }
};

export const handleApiError = (
  error: unknown,
  overrides?: CodeMap,
  fallback: CodeHandler | string = "Forbidden-Access-Error",
): never => {
  if (isHandledError(error)) throw error as Error;

  const err = error as AxiosError<any>;
  const code = err.response?.data?.code as string | undefined;
  const body: string = err.response?.data?.message?.body ?? "";
  const mergedMap = { ...defaultCodeMap, ...overrides };

  if (code) {
    const entry = mergedMap[code];
    showAlert(entry ? normalizeEntry(entry) : normalizeEntry(fallback), body);
  } else {
    alertService.error("cannot-connect-to-server");
  }

  throw new ApiError(
    code ?? "UNKNOWN",
    (err as AxiosError).response?.status ?? 0,
    body,
    error,
  );
};
